import { ACCESS_LEVELS, atLeastLevel } from '@/shared/access';
import type { AccessGroup, AccessLevel, AccessReviewRow, ResolvedDomain } from '@/shared/access';

import type { DepartmentRights } from './departmentRights';

/**
 * What an access review is looking *for*.
 *
 * <p>A review screen that only renders the data is a report. The reviewer's actual job is to find the
 * shapes that are usually wrong, act on them, and know what is left — so the rules that decide "this one
 * needs a look" are pulled out here, kept pure, and used by both the summary counts at the top of the
 * screen and the per-row markers in the listings. One definition, one place to change it, and the number
 * in the summary can never disagree with the rows underneath it.
 */

/** Why a person is worth stopping on. */
export type PersonFlag =
  /** Active, employed, and holds nothing at all — usually onboarded and then forgotten. */
  | 'NO_ACCESS'
  /** Administrator-level anywhere: authority over how the company works, not just over its data. */
  | 'ADMINISTRATOR';

/** Why a department is worth stopping on. */
export type DepartmentFlag =
  /** Confers a capability adr-auth-004 classes SECRET / FINANCIAL / GOVERNANCE. */
  | 'ELEVATED'
  /** Confers ADMIN in at least one domain. */
  | 'ADMINISTRATOR'
  /** Grants real rights but has no direct members — a live grant nobody is using. */
  | 'MEMBERLESS';

/**
 * Two shapes are worth flagging on a person: administrator-level access anywhere, and an active employee
 * with no access at all. Kept in the order a reviewer would read them.
 */
export function personFlags(row: AccessReviewRow): PersonFlag[] {
  const flags: PersonFlag[] = [];
  if (row.status === 'ACTIVE' && row.domains.length === 0) {
    flags.push('NO_ACCESS');
  }
  if (row.domains.some((domain) => atLeastLevel(domain.level, 'ADMIN'))) {
    flags.push('ADMINISTRATOR');
  }
  return flags;
}

export function isPersonFlagged(row: AccessReviewRow): boolean {
  return personFlags(row).length > 0;
}

/**
 * The department equivalent.
 *
 * <p>`MEMBERLESS` only counts when the department actually confers something: an empty department that
 * grants nothing is tidy-up, not a risk, and putting it in the same bucket would bury the ones that
 * matter.
 */
export function departmentFlags(group: AccessGroup, rights: DepartmentRights): DepartmentFlag[] {
  const flags: DepartmentFlag[] = [];
  if (rights.elevated.length > 0) {
    flags.push('ELEVATED');
  }
  if (rights.administers) {
    flags.push('ADMINISTRATOR');
  }
  if (group.memberCount === 0 && !rights.empty) {
    flags.push('MEMBERLESS');
  }
  return flags;
}

/** i18n key for a person flag, used for the row marker's tooltip and the CSV reason column. */
export function personFlagKey(flag: PersonFlag): string {
  return flag === 'NO_ACCESS' ? 'review.flags.noAccess' : 'review.flags.administrator';
}

/** What a person's access adds up to, condensed enough to read in one table cell. */
export interface AccessSummary {
  /** The strongest domains first, capped at `limit`. */
  top: ResolvedDomain[];
  /** How many conferring domains did not fit. */
  more: number;
  /** The strongest level held anywhere; `null` when the person holds nothing. */
  highest: AccessLevel | null;
  /** Total number of domains conferring something. */
  total: number;
}

/**
 * Condenses a review row for the compact list.
 *
 * <p>The matrix answers "what does everyone hold in domain X" at the cost of one column per domain; the
 * list answers "does this person look right" in a fixed width. Sorting strongest-first is what makes the
 * truncation safe — the rights that would worry a reviewer are never the ones hidden behind "+3".
 */
export function summariseAccess(row: AccessReviewRow, limit = 3): AccessSummary {
  const conferring = row.domains
    .filter((domain) => domain.level !== 'NONE')
    .sort(
      (a, b) =>
        ACCESS_LEVELS.indexOf(b.level) - ACCESS_LEVELS.indexOf(a.level) ||
        a.domainCode.localeCompare(b.domainCode),
    );

  return {
    top: conferring.slice(0, limit),
    more: Math.max(conferring.length - limit, 0),
    highest: conferring[0]?.level ?? null,
    total: conferring.length,
  };
}
